import { Mesh as MeshData, Skin, ObjectContainer } from "@spi/Common/Collada";
import { Context } from "../Engine";
import { VertexBuffer } from "./VertexBuffer";
import { Collection } from "@spi/Common/Geometry";
import { RenderObject } from "./RenderObject";
import { AnimationDataTexture, ITexture } from "../Texture";
import { Animation } from '@spi/Common/Animation';

export abstract class Mesh {

    public abstract buffer: VertexBuffer;
    public abstract texture: ITexture;

    public constructor(public reference: string, public ctx: Context) {

    }

    public abstract createInstance(ctx: Context): RenderObject;
}

export class PreCompiledGeometryMesh extends Mesh {
    public buffer: VertexBuffer;    
    public texture: ITexture;

    public bufferMesh(data: Float32Array, vertexLength: number, vertexCount: number, texture?: ITexture) {
        let gl = this.ctx.gl;

        let glVertexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, glVertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);

        this.buffer = new VertexBuffer(glVertexBuffer, gl.TRIANGLES, vertexLength, vertexCount);

        this.texture = texture;
    }

    public createInstance(ctx: Context): RenderObject {
        return new RenderObject(this);
    }

    
}

export class GeometryMesh extends Mesh {
    public buffer: VertexBuffer;
    public texture: ITexture;

    public bufferGeneratedMesh(geoCollection: Collection, compilationPattern = 'pnc') {

        let gl = this.ctx.gl;
        geoCollection.setCompilationOptions({
            compilationPattern: compilationPattern
        })
        let compiled = geoCollection.compile(true);

        let glVertexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, glVertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, compiled.geometryData, gl.STATIC_DRAW);

        this.buffer = new VertexBuffer(glVertexBuffer, gl.TRIANGLES, compiled.vertexLength, compiled.vertexCount);


        this.texture = null;
    }

    public createInstance(): RenderObject {
        return new RenderObject(this);
    }

}

export class ColladaMesh extends Mesh {
    public buffer: VertexBuffer;
    public texture: ITexture;
    private skin: Skin;

    public constructor(reference: string, ctx: Context, private colladaObject: ObjectContainer) {
        super(reference, ctx);
    }

    public createInstance(): RenderObject {
        let scene = this.colladaObject.sceneLibrary[0];
        let controllerNode = scene.getControllerNodes()[0];

        let anim: Animation;

        if (controllerNode) {

            if (!controllerNode.animation) {
                controllerNode.animation = new Animation(scene, this.colladaObject);
                controllerNode.animation.preComputeAnimations(controllerNode);
                controllerNode.animation.dataTexture = new AnimationDataTexture(this.ctx, controllerNode.animation);
            }
            anim = controllerNode.animation;

            //objectMesh = this.engine.meshManager.getMesh(objectType); // controllerNode.controller.skin.geometry.bufferedMesh;
        } else {
            //objectMesh = this.engine.meshManager.getMesh(objectType); // colladaObject.geometryLibrary[0].bufferedMesh;
        }

        let obj = new RenderObject(this);
        obj.animation = anim;
        obj.controllerNode = controllerNode;
        obj.visualScene = scene;

        obj.skinned = !!controllerNode;

        return obj;
    }

    public bufferColladaMesh(colladaMesh: MeshData) {
        let gl = this.ctx.gl;

        let skin = colladaMesh.geometry.skin;
        if (skin) {
            skin.vertexWeights.preComputeSampleOffset();
            this.skin = skin;
        }

        let vertexCount = 0;
        for (let polylist of colladaMesh.polylists) {
            vertexCount += polylist.count * 3;
        }

        // TODO: Include texture coords along with colors
        let vertexSize = 18; // x, y, z, nx, ny, nz, r, g, b, a, (weight, jointIndex)[0 .. 7]

        let vertexData = new Float32Array(vertexCount * vertexSize); // Assume triangulated data so each polygon consists of three vertices

        for (let i = 0; i < vertexData.length; i++) {
            vertexData[i] = -1;
        }

        let vertexIndex = 0;

        for (let polylist of colladaMesh.polylists) {
            let primitiveOffset = 0;

            let vertexInput = polylist.getInput("VERTEX");
            let normalInput = polylist.getInput("NORMAL");
            let texInput = polylist.getInput("TEXCOORD");
            let colorInput = polylist.getInput("COLOR");

            let inputs = [vertexInput, normalInput, texInput, colorInput];
            let offsetCount = 1;
            for (var ip = 0; ip < inputs.length; ip++) {
                if (inputs[ip]) {
                    offsetCount = Math.max(offsetCount, inputs[ip].offset + 1);
                }
            }

            for (let i = 0; i < polylist.count; i++) {

                // Assume triangles for v
                for (let v = 0; v < 3; v++) {

                    let vertexInputOffset = polylist.primitives[primitiveOffset + vertexInput.offset]; // use this offset to get weights and indices for this vertex

                    let c = 0;

                    let vertexSample = vertexInput.sample<Float32Array>(vertexInputOffset);
                    for (let n = 0; n < vertexSample.length; n++) {
                        vertexData[vertexIndex * vertexSize + c++] = vertexSample[n];
                    }

                    let normalSample = normalInput.sample<Float32Array>(polylist.primitives[primitiveOffset + normalInput.offset]);
                    for (let n = 0; n < normalSample.length; n++) {
                        vertexData[vertexIndex * vertexSize + c++] = normalSample[n];
                    }

                    if (texInput) {
                        let texSample = texInput.sample<Float32Array>(polylist.primitives[primitiveOffset + texInput.offset]);
                        for (let n = 0; n < texSample.length; n++) {
                            vertexData[vertexIndex * vertexSize + c++] = texSample[n];
                        }
                    } else if (colorInput) {
                        let colorSample = colorInput.sample(polylist.primitives[primitiveOffset + colorInput.offset]);

                        // Take RGB
                        for (let cv = 0; cv < 3; cv++) {
                            vertexData[vertexIndex * vertexSize + c++] = colorSample[cv];
                        }
                        // Assume 1 for A
                        vertexData[vertexIndex * vertexSize + c++] = 1;
                    } else {
                        // No color or texture, just set some base color
                        vertexData[vertexIndex * vertexSize + c++] = 1;
                        vertexData[vertexIndex * vertexSize + c++] = 1;
                        vertexData[vertexIndex * vertexSize + c++] = 1;
                        vertexData[vertexIndex * vertexSize + c++] = 1;

                    }

                    primitiveOffset += offsetCount;

                    if (skin) {
                        this.getJointData(vertexIndex, vertexInputOffset, vertexData, vertexSize);
                    }
                    vertexIndex++;

                }
            }

        }

        let glVertexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, glVertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertexData, gl.STATIC_DRAW);

        this.buffer = new VertexBuffer(glVertexBuffer, gl.TRIANGLES, vertexSize, vertexCount);


        let scene = this.colladaObject.sceneLibrary[0];

        // TODO: remove this dirty hack
        let geoNodes = scene.rootNodes
            .filter(node => node.subType == 'geometry' && node.geometry && node.geometry.mesh == colladaMesh && node.material);

        if (geoNodes.length > 0) {
            
            let imgRef = geoNodes[0].material.getImageReference();
            if (imgRef) {
                this.texture = this.ctx.engine.materialManager.getTexture(imgRef);
            }
            else {
                this.texture = null;
            }
        }

        if (!this.texture) {

            if (colladaMesh.polylists[0].material) {

                let imgRef = colladaMesh.polylists[0].material.getImageReference();
                if (imgRef) {
                    this.texture = this.ctx.engine.materialManager.getTexture(imgRef);
                }
                else {
                    this.texture = null;
                }
            } else {
                this.texture = null;
            }
        }
    }

    private getJointData(vertexIndex: number, vertexInputOffset: number, vertexData: Float32Array, vertexSize: number) {
        let vertexWeights = this.skin.vertexWeights;

        let jointInput = vertexWeights.getInput("JOINT");
        let weightInput = vertexWeights.getInput("WEIGHT");

        let vcount = vertexWeights.vertexCount[vertexInputOffset]; // number of joints affecting a vertex

        let sampleIndexOffset = vertexWeights.sampleOffsets[vertexInputOffset];

        for (let vc = 0; vc < vcount; vc++) {
            let jointSample = jointInput.sample<Float32Array>(vertexWeights.vertexIndices[sampleIndexOffset + jointInput.offset]);
            let weightSample = weightInput.sample<Float32Array>(vertexWeights.vertexIndices[sampleIndexOffset + weightInput.offset]);

            let jointIndex = jointSample[0];
            let weight = weightSample[0];

            sampleIndexOffset += 2;

            vertexData[vertexIndex * vertexSize + 10 + 4 + vc] = jointIndex;
            vertexData[vertexIndex * vertexSize + 10 + vc] = weight;
        }
    }
}